// F9: CSV export. Spreadsheet apps run cells starting with = + - @ as formulas,
// so text cells are prefixed with an apostrophe (OWASP "CSV injection").
import { typeFor } from "./catalog";
import { contractValue, currentTermEnd, nextNoticeDeadline, peopleLine, type Contract } from "./contract";
import { currencyDigits } from "./money";

export type CsvCell = string | number | null | undefined;

const FORMULA_START = /^[=+\-@\t\r]/;

export function csvCell(value: CsvCell): string {
  if (value === null || value === undefined) return "";
  let s = typeof value === "number" ? (Number.isFinite(value) ? String(value) : "") : String(value);
  if (typeof value === "string" && FORMULA_START.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: readonly (readonly CsvCell[])[]): string {
  return rows.map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

export const CONTRACT_CSV_HEADER = [
  "id",
  "title",
  "type",
  "people",
  "value",
  "currency",
  "start_date",
  "end_date",
  "current_term_end",
  "next_notice_deadline",
  "renewal_type",
  "renewal_term_months",
  "notice_period_days",
  "owner",
  "tags",
  "status",
  "notes",
  "documents",
  "created_at",
  "updated_at",
] as const;

export function contractsToCsv(
  contracts: readonly Contract[],
  today: string,
  ownerName: (id: string | null) => string = (id) => id ?? "",
): string {
  const rows: CsvCell[][] = [[...CONTRACT_CSV_HEADER]];
  for (const c of contracts) {
    if (c.deletedAt) continue;
    const total = contractValue(c);
    const value = total ? (total.minor / 10 ** currencyDigits(total.currency)).toFixed(currencyDigits(total.currency)) : null;
    rows.push([
      c.id,
      c.title,
      typeFor(c.type)?.label ?? null,
      peopleLine(c) || null,
      value,
      c.currency,
      c.startDate,
      c.endDate,
      currentTermEnd(c, today),
      nextNoticeDeadline(c, today),
      c.renewalType,
      c.renewalTermMonths,
      c.noticePeriodDays,
      ownerName(c.ownerUserId),
      c.tags.join("; "),
      c.status,
      c.notes,
      c.documents.map((d) => d.filename).join("; "),
      c.createdAt,
      c.updatedAt,
    ]);
  }
  // BOM so Excel opens UTF-8 (₹, é, Devanagari) correctly.
  return "﻿" + toCsv(rows);
}
